import type { DiscussionThread, RealtimeDiscussionEvent } from "@/types";

export interface ThreadGroup {
  label: string;
  threads: DiscussionThread[];
}

export interface ThreadSection {
  key: "active" | "archived";
  label: string;
  groups: ThreadGroup[];
  emptyMessage: string;
}

export function filterThreadsByQuery(
  threads: DiscussionThread[],
  searchValue: string,
): DiscussionThread[] {
  const query = searchValue.trim().toLowerCase();
  if (!query) return threads;

  return threads.filter((thread) => {
    const title = thread.title.toLowerCase();
    const tags = thread.tags.join(" ").toLowerCase();
    return title.includes(query) || tags.includes(query);
  });
}

export function sortThreadsByLastActivity(
  threads: DiscussionThread[],
): DiscussionThread[] {
  return [...threads].sort(
    (left, right) =>
      new Date(right.updated_at).getTime() - new Date(left.updated_at).getTime(),
  );
}

export function groupThreadsByActivity(threads: DiscussionThread[]): ThreadGroup[] {
  const orderedThreads = sortThreadsByLastActivity(threads);
  if (orderedThreads.length === 0) return [];
  return [{ label: "", threads: orderedThreads }];
}

export function isCartThread(thread: DiscussionThread): boolean {
  return thread.tags.some((tag) => tag.toLowerCase().includes("compra"));
}

export function threadBadgeDate(thread: DiscussionThread): string {
  const updatedAt = new Date(thread.updated_at).getTime();
  if (Number.isNaN(updatedAt)) return "";

  const diffDays = Math.floor((Date.now() - updatedAt) / 86_400_000);
  if (diffDays < 7) return "";

  return new Date(thread.updated_at).toLocaleDateString("es-AR", {
    day: "numeric",
    month: "short",
  });
}

export function isThreadListRealtimeEvent(
  message: RealtimeDiscussionEvent,
): boolean {
  return (
    message.type === "discussion.message.created" ||
    message.type === "discussion.thread.created" ||
    message.type === "discussion.thread.archived" ||
    message.type === "discussion.thread.unarchived" ||
    message.type === "discussion.thread.access.changed"
  );
}
